/**
 * Mordeczka.eu Admin - API Services
 *
 * HTTP client configuration and API service functions.
 */

import axios, { AxiosError } from "axios";
import type {
  PaginatedResponse,
  PaginationParams,
  Language,
  LanguageCreate,
  LanguageUpdate,
  Nationality,
  NationalityCreate,
  NationalityUpdate,
  Tag,
  TagCreate,
  TagUpdate,
  Genre,
  GenreCreate,
  GenreUpdate,
  LiteraryGenre,
  LiteraryGenreCreate,
  LiteraryGenreUpdate,
  ImageCategory,
  ImageCategoryCreate,
  ImageCategoryUpdate,
  StreamingPlatform,
  StreamingPlatformCreate,
  StreamingPlatformUpdate,
  Author,
  AuthorCreate,
  AuthorUpdate,
  Publisher,
  PublisherCreate,
  PublisherUpdate,
  Director,
  DirectorCreate,
  DirectorUpdate,
  User,
  UserCreate,
  UserUpdate,
  Role,
  RoleCreate,
  RoleUpdate,
  Work,
  WorkCreate,
  WorkUpdate,
  WorkFilters,
  Image,
  AuditLog,
  Cinema,
  CinemaCreate,
  CinemaUpdate,
  CinemaHall,
  CinemaHallCreate,
  CinemaHallUpdate,
  SubscriptionType,
  SubscriptionTypeCreate,
  SubscriptionTypeUpdate,
  PurchaseItemType,
  PurchaseSpendingRow,
  UserPurchase,
  UserPurchaseCreate,
  UserPurchaseUpdate,
  WatchBrand,
  WatchBrandCreate,
  WatchBrandUpdate,
  PerfumeBrand,
  PerfumeBrandCreate,
  PerfumeBrandUpdate,
} from "../types";

// API base URL from environment
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:9006";

// Axios instance with default configuration
export const apiClient = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000,
  withCredentials: true, // Enable cookies for auth
});

// Response interceptor (for error handling)
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Could redirect to login here if needed
      // window.location.href = '/login';
    }
    return Promise.reject(error);
  },
);

// =============================================================================
// Generic CRUD Functions
// =============================================================================
async function fetchPaginated<T>(
  endpoint: string,
  params: PaginationParams = {},
): Promise<PaginatedResponse<T>> {
  const { data } = await apiClient.get<PaginatedResponse<T>>(endpoint, {
    params: {
      page: params.page || 1,
      page_size: params.page_size || 50,
      sort: params.sort,
      order: params.order,
      search: params.search,
      has_works: (params as any).has_works,
      has_movies: (params as any).has_movies,
    },
  });
  return data;
}

async function fetchOne<T>(endpoint: string, id: string): Promise<T> {
  const { data } = await apiClient.get<T>(`${endpoint}/${id}`);
  return data;
}

async function createOne<T, C>(endpoint: string, payload: C): Promise<T> {
  const { data } = await apiClient.post<T>(endpoint, payload);
  return data;
}

async function updateOne<T, U>(
  endpoint: string,
  id: string,
  payload: U,
): Promise<T> {
  const { data } = await apiClient.patch<T>(`${endpoint}/${id}`, payload);
  return data;
}

async function deleteOne(endpoint: string, id: string): Promise<void> {
  await apiClient.delete(`${endpoint}/${id}`);
}

// =============================================================================
// Authentication API
// =============================================================================

export interface LoginRequest {
  email: string;
  password: string;
}

export interface CurrentUserResponse {
  id: string;
  email: string;
  display_name: string | null;
  role_name: string | null;
  is_active: boolean;
}

export const authApi = {
  login: async (
    email: string,
    password: string,
  ): Promise<CurrentUserResponse> => {
    try {
      const { data } = await apiClient.post<CurrentUserResponse>(
        "/auth/login",
        { email, password },
      );
      return data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // Server returned an error response with details
        if (error.response?.data?.detail) {
          throw new Error(error.response.data.detail);
        }
        // Network error, timeout, or server unavailable
        if (error.code === "ECONNABORTED" || error.code === "ERR_NETWORK") {
          throw new Error("Nie można połączyć się z serwerem");
        }
      }
      throw new Error("Wystąpił błąd podczas logowania");
    }
  },

  logout: async (): Promise<void> => {
    await apiClient.post("/auth/logout");
  },

  me: async (): Promise<CurrentUserResponse> => {
    const { data } = await apiClient.get<CurrentUserResponse>("/auth/me");
    return data;
  },
};

// =============================================================================
// Language API
// =============================================================================

export const languagesApi = {
  list: (params?: PaginationParams) =>
    fetchPaginated<Language>("/languages", params),
  get: (id: string) => fetchOne<Language>("/languages", id),
  create: (data: LanguageCreate) =>
    createOne<Language, LanguageCreate>("/languages", data),
  update: (id: string, data: LanguageUpdate) =>
    updateOne<Language, LanguageUpdate>("/languages", id, data),
  delete: (id: string) => deleteOne("/languages", id),
};

// =============================================================================
// Tag API
// =============================================================================

export const tagsApi = {
  list: (params?: PaginationParams) => fetchPaginated<Tag>("/tags", params),
  get: (id: string) => fetchOne<Tag>("/tags", id),
  create: (data: TagCreate) => createOne<Tag, TagCreate>("/tags", data),
  update: (id: string, data: TagUpdate) =>
    updateOne<Tag, TagUpdate>("/tags", id, data),
  delete: (id: string) => deleteOne("/tags", id),
};

// =============================================================================
// Genre API
// =============================================================================

export const genresApi = {
  list: (params?: PaginationParams) => fetchPaginated<Genre>("/genres", params),
  get: (id: string) => fetchOne<Genre>("/genres", id),
  create: (data: GenreCreate) => createOne<Genre, GenreCreate>("/genres", data),
  update: (id: string, data: GenreUpdate) =>
    updateOne<Genre, GenreUpdate>("/genres", id, data),
  delete: (id: string) => deleteOne("/genres", id),
};

// =============================================================================
// Literary Genre API
// =============================================================================

export const literaryGenresApi = {
  list: (params?: PaginationParams) =>
    fetchPaginated<LiteraryGenre>("/literary-genres", params),
  get: (id: string) => fetchOne<LiteraryGenre>("/literary-genres", id),
  create: (data: LiteraryGenreCreate) =>
    createOne<LiteraryGenre, LiteraryGenreCreate>("/literary-genres", data),
  update: (id: string, data: LiteraryGenreUpdate) =>
    updateOne<LiteraryGenre, LiteraryGenreUpdate>("/literary-genres", id, data),
  delete: (id: string) => deleteOne("/literary-genres", id),
};

// =============================================================================
// Image Categories API
// =============================================================================

export const imageCategoriesApi = {
  list: (params?: PaginationParams) =>
    fetchPaginated<ImageCategory>("/image-categories", params),
  /** Lista kategorii bez logowania (publiczny endpoint) – do dropdownów na stronie Obrazki */
  listPublic: async (): Promise<ImageCategory[]> => {
    const { data } = await apiClient.get<ImageCategory[]>("/public/image-categories");
    return data;
  },
  get: (id: string) => fetchOne<ImageCategory>("/image-categories", id),
  create: (data: ImageCategoryCreate) =>
    createOne<ImageCategory, ImageCategoryCreate>("/image-categories", data),
  update: (id: string, data: ImageCategoryUpdate) =>
    updateOne<ImageCategory, ImageCategoryUpdate>("/image-categories", id, data),
  delete: (id: string) => deleteOne("/image-categories", id),
};

// =============================================================================
// Streaming Platform API
// =============================================================================

export const streamingPlatformsApi = {
  list: (params?: PaginationParams) =>
    fetchPaginated<StreamingPlatform>("/streaming-platforms", params),
  get: (id: string) => fetchOne<StreamingPlatform>("/streaming-platforms", id),
  create: (data: StreamingPlatformCreate) =>
    createOne<StreamingPlatform, StreamingPlatformCreate>(
      "/streaming-platforms",
      data,
    ),
  update: (id: string, data: StreamingPlatformUpdate) =>
    updateOne<StreamingPlatform, StreamingPlatformUpdate>(
      "/streaming-platforms",
      id,
      data,
    ),
  delete: (id: string) => deleteOne("/streaming-platforms", id),
};

// =============================================================================
// Nationality API
// =============================================================================

export const nationalitiesApi = {
  list: (params?: PaginationParams) =>
    fetchPaginated<Nationality>("/nationalities", params),
  get: (id: string) => fetchOne<Nationality>("/nationalities", id),
  create: (data: NationalityCreate) =>
    createOne<Nationality, NationalityCreate>("/nationalities", data),
  update: (id: string, data: NationalityUpdate) =>
    updateOne<Nationality, NationalityUpdate>("/nationalities", id, data),
  delete: (id: string) => deleteOne("/nationalities", id),
};

// =============================================================================
// Role API
// =============================================================================

export const rolesApi = {
  list: (params?: PaginationParams) => fetchPaginated<Role>("/roles", params),
  get: (id: string) => fetchOne<Role>("/roles", id),
  create: (data: RoleCreate) => createOne<Role, RoleCreate>("/roles", data),
  update: (id: string, data: RoleUpdate) =>
    updateOne<Role, RoleUpdate>("/roles", id, data),
  delete: (id: string) => deleteOne("/roles", id),
};

// =============================================================================
// User API
// =============================================================================

export const usersApi = {
  list: (params?: PaginationParams) => fetchPaginated<User>("/users", params),
  get: (id: string) => fetchOne<User>("/users", id),
  create: (data: UserCreate) => createOne<User, UserCreate>("/users", data),
  update: (id: string, data: UserUpdate) =>
    updateOne<User, UserUpdate>("/users", id, data),
  delete: (id: string) => deleteOne("/users", id),
};

// =============================================================================
// Work API (with filters)
// =============================================================================

export const worksApi = {
  list: async (
    params?: PaginationParams & WorkFilters,
  ): Promise<PaginatedResponse<Work>> => {
    const { data } = await apiClient.get<PaginatedResponse<Work>>("/works", {
      params: {
        page: params?.page || 1,
        page_size: params?.page_size || 50,
        type: params?.type,
        search: params?.search,
        year: params?.year,
        sort: params?.sort,
        order: params?.order,
      },
    });
    return data;
  },
  get: (id: string) => fetchOne<Work>("/works", id),
  create: (data: WorkCreate) => createOne<Work, WorkCreate>("/works", data),
  update: (id: string, data: WorkUpdate) =>
    updateOne<Work, WorkUpdate>("/works", id, data),
  delete: (id: string) => deleteOne("/works", id),

  // Type-specific lists
  listBooks: (params?: PaginationParams) =>
    apiClient
      .get<PaginatedResponse<Work>>("/works/books", { params })
      .then((r) => r.data),
  listMovies: (params?: PaginationParams) =>
    apiClient
      .get<PaginatedResponse<Work>>("/works/movies", { params })
      .then((r) => r.data),
  listSeries: (params?: PaginationParams) =>
    apiClient
      .get<PaginatedResponse<Work>>("/works/series", { params })
      .then((r) => r.data),
};

export const workImagesApi = {
  list: (workId: string) =>
    apiClient.get(`/works/${workId}/images`).then((r) => r.data),
  add: (
    workId: string,
    payload: { image_id: string; position?: number | null },
  ) => apiClient.post(`/works/${workId}/images`, payload).then((r) => r.data),
  update: (workId: string, imageId: string, payload: { position: number }) =>
    apiClient
      .patch(`/works/${workId}/images/${imageId}`, payload)
      .then((r) => r.data),
  reorder: (workId: string, imageIds: string[]) =>
    apiClient
      .put(`/works/${workId}/images/order`, imageIds)
      .then((r) => r.data),
  delete: (workId: string, imageId: string) =>
    apiClient.delete(`/works/${workId}/images/${imageId}`),
};

export interface MyLibraryYearsResponse {
  years: number[];
}

export interface MyLibraryStatsResponse {
  year: number;
  total_completed: number;
  books_completed: number;
  movies_completed: number;
  series_completed: number;
  pages_read: number;
  minutes_watched_movies: number;
  minutes_watched_series: number;
  minutes_watched: number;
}

export interface MyLibraryEntryCreateRequest {
  work_id: string;
  season_id?: string | null;
  finished_at: string;
  rating?: number;
  review?: string;
  private_notes?: string | null;
  storage_location?: string | null;
  cinema_id?: string | null;
  cinema_hall_id?: string | null;
  screening_at?: string | null;
  ticket_price?: number | null;
  is_subscription?: boolean;
  subscription_type_id?: string | null;
  seat_row?: string | null;
  seat_number?: string | null;
  is_public?: boolean;
}

export interface MyLibraryEntryCreateResponse {
  id: string;
}

export interface MyLibraryEntryListItem {
  id: string;
  work_id: string;
  season_id: string | null;
  work_title: string;
  work_type: string;
  finished_at: string;
  rating: number | null;
  review: string | null;
  private_notes: string | null;
  is_public: boolean;
  page_count: number | null;
  storage_location: string | null;
  price: number | null;
  duration_minutes: number | null;
  season_number: number | null;
  season_duration_minutes: number | null;
  screening_at: string | null;
  ticket_price: number | null;
  is_subscription: boolean | null;
  subscription_type_id: string | null;
  seat_row: string | null;
  seat_number: string | null;
  cinema_id: string | null;
  cinema_hall_id: string | null;
  cinema_name: string | null;
  cinema_hall_name: string | null;
  cover_image_id?: string | null;
  streaming_platform_name?: string | null;
  streaming_platform_logo_url?: string | null;
}

export interface MyLibraryEntriesResponse {
  items: MyLibraryEntryListItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface MyLibraryMonthlyStatsItem {
  month: number;
  total_completed: number;
  books_completed: number;
  movies_completed: number;
  series_completed: number;
  pages_read: number;
  minutes_watched_movies: number;
  minutes_watched_series: number;
  minutes_watched: number;
}

export interface MyLibraryMonthlyStatsResponse {
  year: number;
  items: MyLibraryMonthlyStatsItem[];
}

export interface MyLibraryEntryUpdateRequest {
  finished_at?: string;
  rating?: number;
  review?: string;
  private_notes?: string | null;
  is_public?: boolean;
  storage_location?: string | null;
  price?: number | null;
  cinema_id?: string | null;
  cinema_hall_id?: string | null;
  screening_at?: string | null;
  ticket_price?: number | null;
  is_subscription?: boolean;
  subscription_type_id?: string | null;
  seat_row?: string | null;
  seat_number?: string | null;
}

export const myLibraryApi = {
  listYears: async (): Promise<MyLibraryYearsResponse> => {
    const { data } =
      await apiClient.get<MyLibraryYearsResponse>("/my-library/years");
    return data;
  },
  getStats: async (params: { year: number; month?: number }): Promise<MyLibraryStatsResponse> => {
    const { data } = await apiClient.get<MyLibraryStatsResponse>(
      "/my-library/stats",
      { params },
    );
    return data;
  },
  getMonthly: async (year: number): Promise<MyLibraryMonthlyStatsResponse> => {
    const { data } = await apiClient.get<MyLibraryMonthlyStatsResponse>(
      "/my-library/monthly",
      { params: { year } },
    );
    return data;
  },
  listEntries: async (params: {
    year?: number;
    type?: string;
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<MyLibraryEntriesResponse> => {
    const { data } = await apiClient.get<MyLibraryEntriesResponse>(
      "/my-library/entries",
      { params },
    );
    return data;
  },
  getEntry: async (id: string): Promise<MyLibraryEntryListItem> => {
    const { data } = await apiClient.get<MyLibraryEntryListItem>(
      `/my-library/entries/${id}`,
    );
    return data;
  },
  createEntry: async (
    payload: MyLibraryEntryCreateRequest,
  ): Promise<MyLibraryEntryCreateResponse> => {
    const { data } = await apiClient.post<MyLibraryEntryCreateResponse>(
      "/my-library/entries",
      payload,
    );
    return data;
  },
  updateEntry: async (
    id: string,
    payload: MyLibraryEntryUpdateRequest,
  ): Promise<MyLibraryEntryListItem> => {
    const { data } = await apiClient.patch<MyLibraryEntryListItem>(
      `/my-library/entries/${id}`,
      payload,
    );
    return data;
  },
  deleteEntry: async (id: string): Promise<void> => {
    await apiClient.delete(`/my-library/entries/${id}`);
  },
  getDetailedStats: async (params: {
    year?: number;
    month?: number;
  }): Promise<DetailedStatsResponse> => {
    const { data } = await apiClient.get<DetailedStatsResponse>(
      "/my-library/detailed-stats",
      { params },
    );
    return data;
  },
};

// Detailed Stats Types
export interface PlatformStatItem {
  platform_name: string;
  series_count: number;
  seasons_count: number;
  total_minutes: number;
}

export interface PublisherStatItem {
  publisher_name: string;
  books_count: number;
  total_pages: number;
}

export interface CinemaStatItem {
  cinema_name: string;
  movies_count: number;
  total_minutes: number;
  total_ticket_cost: number;
}

export interface CinemaHallStatItem {
  cinema_name: string;
  hall_name: string;
  movies_count: number;
  total_minutes: number;
  total_ticket_cost: number;
}

export interface RatingAvgItem {
  avg_rating: number;
  rated_count: number;
}

export interface TopRatedItem {
  title: string;
  work_type: string;
  rating: number;
  finished_at: string | null;
}

export interface RatingDistribution {
  distribution: Record<number, number>;
  averages: Record<string, RatingAvgItem>;
  top_rated: TopRatedItem[];
  top_rated_by_type: Record<string, TopRatedItem[]>;
}

export interface GenreStatItem {
  genre_name: string;
  works_count: number;
}

export interface DirectorStatItem {
  director_name: string;
  movies_count: number;
  total_minutes: number;
}

export interface MovieGenreStatItem {
  genre_name: string;
  movies_count: number;
  total_minutes: number;
}

export interface MovieNationalityStatItem {
  nationality_name: string;
  nationality_flag_url?: string | null;
  movies_count: number;
  total_minutes: number;
}

export interface SeriesNationalityStatItem {
  nationality_name: string;
  nationality_flag_url?: string | null;
  series_count: number;
  seasons_count: number;
  total_minutes: number;
}

export interface DetailedStatsResponse {
  year: number | null;
  month: number | null;
  series_by_platform: PlatformStatItem[];
  books_by_publisher: PublisherStatItem[];
  movies_by_cinema: CinemaStatItem[];
  movies_by_cinema_hall: CinemaHallStatItem[];
  movies_by_director: DirectorStatItem[];
  movies_by_genre: MovieGenreStatItem[];
  movies_by_nationality: MovieNationalityStatItem[];
  series_by_nationality: SeriesNationalityStatItem[];
  rating_stats: RatingDistribution;
  genre_breakdown: GenreStatItem[];
  book_purchase_costs: BookPurchaseCosts;
}

export interface BookPurchaseCostItem {
  title: string;
  price: number;
  finished_at: string | null;
}

export interface BookPurchaseCosts {
  items: BookPurchaseCostItem[];
  total_cost: number;
  books_count: number;
  avg_price: number;
}

// =============================================================================
// Series Seasons API
// =============================================================================

export interface SeasonCreate {
  season_number: number;
  year?: number | null;
  episodes_count?: number | null;
  episode_duration_minutes?: number | null;
  total_duration_minutes?: number | null;
}

export interface SeasonUpdate {
  season_number?: number;
  year?: number | null;
  episodes_count?: number | null;
  episode_duration_minutes?: number | null;
  total_duration_minutes?: number | null;
}

export interface SeasonResponse {
  id: string;
  series_work_id: string;
  season_number: number;
  year?: number | null;
  episodes_count?: number | null;
  episode_duration_minutes?: number | null;
  total_duration_minutes?: number | null;
}

export const seasonsApi = {
  list: async (workId: string): Promise<SeasonResponse[]> => {
    const response = await apiClient.get<SeasonResponse[]>(
      `/works/${workId}/seasons`,
    );
    return response.data;
  },
  create: async (
    workId: string,
    data: SeasonCreate,
  ): Promise<SeasonResponse> => {
    const response = await apiClient.post<SeasonResponse>(
      `/works/${workId}/seasons`,
      data,
    );
    return response.data;
  },
  update: async (
    workId: string,
    seasonId: string,
    data: SeasonUpdate,
  ): Promise<SeasonResponse> => {
    const response = await apiClient.patch<SeasonResponse>(
      `/works/${workId}/seasons/${seasonId}`,
      data,
    );
    return response.data;
  },
  delete: async (workId: string, seasonId: string): Promise<void> => {
    await apiClient.delete(`/works/${workId}/seasons/${seasonId}`);
  },
};

// =============================================================================
// Purchases API
// =============================================================================

export const purchasesApi = {
  list: (params?: PaginationParams & { item_type?: PurchaseItemType }) =>
    apiClient
      .get<PaginatedResponse<UserPurchase>>("/purchases", {
        params: {
          page: params?.page || 1,
          page_size: params?.page_size || 50,
          search: params?.search,
          item_type: params?.item_type,
        },
      })
      .then((r) => r.data),
  summary: (params?: { item_type?: PurchaseItemType }) =>
    apiClient
      .get<{ items: PurchaseSpendingRow[] }>("/purchases/summary", {
        params: { item_type: params?.item_type },
      })
      .then((r) => r.data),
  get: (id: string) => fetchOne<UserPurchase>("/purchases", id),
  create: (data: UserPurchaseCreate) =>
    createOne<UserPurchase, UserPurchaseCreate>("/purchases", data),
  update: (id: string, data: UserPurchaseUpdate) =>
    updateOne<UserPurchase, UserPurchaseUpdate>("/purchases", id, data),
  delete: (id: string) => deleteOne("/purchases", id),
};

// =============================================================================
// Watch Brand API
// =============================================================================

export const watchBrandsApi = {
  list: (params?: PaginationParams) =>
    fetchPaginated<WatchBrand>("/watch-brands", params),
  get: (id: string) => fetchOne<WatchBrand>("/watch-brands", id),
  create: (data: WatchBrandCreate) =>
    createOne<WatchBrand, WatchBrandCreate>("/watch-brands", data),
  update: (id: string, data: WatchBrandUpdate) =>
    updateOne<WatchBrand, WatchBrandUpdate>("/watch-brands", id, data),
  delete: (id: string) => deleteOne("/watch-brands", id),
};

// =============================================================================
// Perfume Brand API
// =============================================================================

export const perfumeBrandsApi = {
  list: (params?: PaginationParams) =>
    fetchPaginated<PerfumeBrand>("/perfume-brands", params),
  get: (id: string) => fetchOne<PerfumeBrand>("/perfume-brands", id),
  create: (data: PerfumeBrandCreate) =>
    createOne<PerfumeBrand, PerfumeBrandCreate>("/perfume-brands", data),
  update: (id: string, data: PerfumeBrandUpdate) =>
    updateOne<PerfumeBrand, PerfumeBrandUpdate>("/perfume-brands", id, data),
  delete: (id: string) => deleteOne("/perfume-brands", id),
};

// =============================================================================
// Datasource API (External scraping service)
// =============================================================================

export interface DatasourceRequest {
  title: string;
}

export type MediaType = "book" | "movie" | "series";

// Response from datasource API for books
export interface DatasourceBookResponse {
  status: "success" | "error";
  data?: {
    title?: string;
    original_title?: string | null;
    authors?: string[];
    isbn_13?: string;
    publisher?: string;
    publication_year?: string;
    page_count?: number;
    description_snippet?: string;
    description_summary?: string;
    genres?: string[];
    is_ebook_data?: boolean;
    cover_url?: string;
  };
  meta?: {
    sources?: string[];
    processing_time_ms?: number;
  };
  error?: string;
}

// Response from datasource API for movies
export interface DatasourceMovieResponse {
  status: "success" | "error";
  data?: {
    title?: string;
    original_title?: string | null;
    director?: string; // API returns single 'director' field
    directors?: string[]; // Alternative: array of directors
    cast?: string[];
    year?: string; // API returns 'year' instead of 'release_year'
    release_year?: string; // Alternative field name
    runtime?: number; // API returns 'runtime' instead of 'duration_minutes'
    duration_minutes?: number; // Alternative field name
    original_language?: string | null;
    original_language_code?: string | null;
    production_countries?: string[];
    production_country_codes?: string[];
    description?: string;
    genres?: string[];
    rating?: number;
    cover_url?: string;
    poster_path?: string;
    poster_url?: string;
    image?: string;
  };
  meta?: {
    sources?: string[];
    processing_time_ms?: number;
  };
  error?: string;
}

// Response from datasource API for series
export interface DatasourceSeriesResponse {
  status: "success" | "error";
  data?: {
    title?: string;
    original_title?: string | null;
    creators?: string[];
    cast?: string[];
    year?: string; // API returns 'year' for start year
    end_year?: string | null;
    number_of_seasons?: number; // API field name
    number_of_episodes?: number; // API field name
    description?: string;
    genres?: string[];
    streaming_platforms?: string[];
    rating?: number;
    cover_url?: string;
    poster_path?: string;
    poster_url?: string;
    image?: string;
  };
  meta?: {
    sources?: string[];
    processing_time_ms?: number;
  };
  error?: string;
}

// Normalized response for books
export interface BookInfoResponse {
  title?: string | null;
  original_title?: string | null;
  release_year?: number | null;
  description?: string | null;
  isbn_10?: string | null;
  isbn_13?: string | null;
  page_count?: number | null;
  publisher?: string | null;
  publisher_id?: string | null;
  suggested_publisher?: string | null;
  author_ids?: string[];
  suggested_authors?: string[];
  literary_genre_ids?: string[];
  suggested_genres?: string[];
  language_code?: string | null;
  cover_url?: string | null;
  image_candidates?: DatasourceImageCandidate[];
}

// Normalized response for movies
export interface MovieInfoResponse {
  title?: string | null;
  original_title?: string | null;
  release_year?: number | null;
  description?: string | null;
  duration_minutes?: number | null;
  original_language?: string | null;
  original_language_code?: string | null;
  production_countries?: string[];
  production_country_codes?: string[];
  nationality_id?: string;
  director_id?: string;
  director_ids?: string[];
  suggested_directors?: string[];
  genre_ids?: string[];
  suggested_genres?: string[];
  cover_url?: string | null;
  image_candidates?: DatasourceImageCandidate[];
  additional_image_urls?: string[];
}

// Normalized response for series
export interface SeriesInfoResponse {
  title?: string | null;
  original_title?: string | null;
  start_year?: number | null;
  end_year?: number | null;
  seasons_count?: number | null;
  episodes_count?: number | null;
  description?: string | null;
  creator_ids?: string[];
  suggested_creators?: string[];
  genre_ids?: string[];
  suggested_genres?: string[];
  streaming_platform_ids?: string[];
  suggested_streaming_platforms?: string[];
  cover_url?: string | null;
  image_candidates?: DatasourceImageCandidate[];
}

export interface DatasourceImageCandidate {
  image_id: string;
  source: string;
  url: string;
  thumbnail_url?: string | null;
  label?: string | null;
  kind?: string | null;
  width?: number | null;
  height?: number | null;
  language?: string | null;
  score?: number | null;
}

export interface DatasourceCandidate {
  candidate_id: string;
  source: string;
  type: "book" | "movie" | "series";
  title?: string | null;
  original_title?: string | null;
  year?: string | null;
  description?: string | null;
  creators?: string[];
  source_url?: string | null;
  external_ids?: Record<string, unknown>;
  thumbnail_url?: string | null;
  image_candidates?: DatasourceImageCandidate[];
  confidence?: number | null;
}

export interface DatasourceSearchResponse {
  status: "success" | "no_results" | "error" | "disabled";
  items?: DatasourceCandidate[];
  meta?: {
    sources?: string[];
    processing_time_ms?: number;
  };
  message?: string;
  error?: string;
}

export interface DatasourceDetailsRequest {
  title?: string;
  isbn?: string;
  candidate: DatasourceCandidate;
  selected_image_url?: string | null;
  use_web_fallback?: boolean;
}

// Legacy alias
export interface BookInfoRequest {
  title?: string;
  isbn?: string;
}

type DatasourceStreamEnvelope = {
  status?: string;
  error?: string;
  message?: string;
  data?: unknown;
};

function normalizeOriginalTitle(originalTitle?: string | null, title?: string | null): string | null {
  const original = originalTitle?.trim();
  const localTitle = title?.trim();
  if (!original || original === localTitle) {
    return null;
  }
  return original;
}

async function readDatasourceStream<TResult, T>(
  response: Response,
  normalize: (result: TResult) => T,
  onProgress?: (message: string) => void,
): Promise<T> {
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const reader = response.body?.getReader();
  const decoder = new TextDecoder();
  if (!reader) {
    throw new Error("No response body");
  }

  let buffer = "";
  let currentEvent: string | null = null;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (line.startsWith("event: ")) {
        currentEvent = line.slice(7).trim();
        continue;
      }
      if (!line.startsWith("data: ")) continue;

      const eventData = line.slice(6);
      try {
        const result = JSON.parse(eventData) as TResult & DatasourceStreamEnvelope;
        if (currentEvent === "progress") {
          if (onProgress) onProgress(result.message || eventData);
          continue;
        }
        if (result.status === "error" || result.error) {
          throw new Error(result.error || result.message || "Failed to fetch datasource details");
        }
        if (currentEvent === "done" || result.data || result.status === "success" || result.status === "no_results") {
          return normalize(result);
        }
        if (onProgress) onProgress(eventData);
      } catch (error) {
        if (error instanceof Error && eventData.trim().startsWith("{")) {
          throw error;
        }
        if (onProgress) onProgress(eventData);
      }
    }
  }

  throw new Error("Datasource stream ended without result");
}

function normalizeBookInfo(result: DatasourceBookResponse & { image_candidates?: DatasourceImageCandidate[] }): BookInfoResponse {
  if (result.status === "error" || !result.data) {
    throw new Error(result.error || "Failed to fetch book data");
  }
  return {
    title: result.data.title,
    original_title: normalizeOriginalTitle(result.data.original_title, result.data.title),
    release_year: result.data.publication_year ? parseInt(result.data.publication_year) : null,
    description: result.data.description_summary || result.data.description_snippet,
    isbn_10: null,
    isbn_13: result.data.isbn_13,
    page_count: result.data.page_count,
    publisher: result.data.publisher,
    publisher_id: null,
    suggested_publisher: result.data.publisher,
    author_ids: [],
    suggested_authors: result.data.authors || [],
    literary_genre_ids: [],
    suggested_genres: result.data.genres || [],
    language_code: "pl",
    cover_url: result.data.cover_url || null,
    image_candidates: result.image_candidates || [],
  };
}

function normalizeMovieInfo(result: DatasourceMovieResponse & { image_candidates?: DatasourceImageCandidate[] }): MovieInfoResponse {
  if (result.status === "error" || !result.data) {
    throw new Error(result.error || "Failed to fetch movie data");
  }
  const yearValue = result.data.year || result.data.release_year;
  const runtimeValue = result.data.runtime ?? result.data.duration_minutes;
  let directorsArray: string[] = [];
  if (result.data.directors && result.data.directors.length > 0) {
    directorsArray = result.data.directors;
  } else if (result.data.director) {
    directorsArray = [result.data.director];
  }
  return {
    title: result.data.title,
    original_title: normalizeOriginalTitle(result.data.original_title, result.data.title),
    release_year: yearValue ? parseInt(yearValue) : null,
    description: result.data.description,
    duration_minutes: runtimeValue ?? null,
    original_language: result.data.original_language || null,
    original_language_code: result.data.original_language_code || null,
    production_countries: result.data.production_countries || [],
    production_country_codes: result.data.production_country_codes || [],
    director_ids: [],
    suggested_directors: directorsArray,
    genre_ids: [],
    suggested_genres: result.data.genres || [],
    cover_url:
      result.data.cover_url ||
      result.data.poster_path ||
      result.data.poster_url ||
      result.data.image,
    image_candidates: result.image_candidates || [],
  };
}

function normalizeSeriesInfo(result: DatasourceSeriesResponse & { image_candidates?: DatasourceImageCandidate[] }): SeriesInfoResponse {
  if (result.status === "error" || !result.data) {
    throw new Error(result.error || "Failed to fetch series data");
  }
  return {
    title: result.data.title,
    original_title: normalizeOriginalTitle(result.data.original_title, result.data.title),
    start_year: result.data.year ? parseInt(result.data.year) : null,
    end_year: result.data.end_year ? parseInt(result.data.end_year) : null,
    seasons_count: result.data.number_of_seasons,
    episodes_count: result.data.number_of_episodes,
    description: result.data.description,
    creator_ids: [],
    suggested_creators: result.data.creators || [],
    genre_ids: [],
    suggested_genres: result.data.genres || [],
    streaming_platform_ids: [],
    suggested_streaming_platforms: result.data.streaming_platforms || [],
    cover_url:
      result.data.cover_url ||
      result.data.poster_path ||
      result.data.poster_url ||
      result.data.image,
    image_candidates: result.image_candidates || [],
  };
}

export const datasourceApi = {
  fetchBookCandidates: async (
    data: BookInfoRequest,
  ): Promise<DatasourceSearchResponse> => {
    const DATASOURCE_URL =
      import.meta.env.VITE_DATASOURCE_API_URL || `${API_URL}/api/v1/datasource`;
    const response = await fetch(`${DATASOURCE_URL}/book/search`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  },

  fetchBookDetailsStreaming: async (
    data: DatasourceDetailsRequest,
    onProgress?: (message: string) => void,
  ): Promise<BookInfoResponse> => {
    const DATASOURCE_URL =
      import.meta.env.VITE_DATASOURCE_API_URL || `${API_URL}/api/v1/datasource`;
    const response = await fetch(`${DATASOURCE_URL}/book/details/stream`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    return readDatasourceStream(response, normalizeBookInfo, onProgress);
  },

  fetchBookInfoStreaming: async (
    data: BookInfoRequest,
    onProgress?: (message: string) => void,
  ): Promise<BookInfoResponse> => {
    const DATASOURCE_URL =
      import.meta.env.VITE_DATASOURCE_API_URL || `${API_URL}/api/v1/datasource`;
    const response = await fetch(`${DATASOURCE_URL}/book/stream`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    return readDatasourceStream(response, normalizeBookInfo, onProgress);
  },

  fetchBookInfo: async (data: BookInfoRequest): Promise<BookInfoResponse> => {
    const DATASOURCE_URL =
      import.meta.env.VITE_DATASOURCE_API_URL || `${API_URL}/api/v1/datasource`;
    const response = await fetch(`${DATASOURCE_URL}/book`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const result: DatasourceBookResponse = await response.json();
    return normalizeBookInfo(result);
  },
};

// Keep aiApi for backward compatibility// Keep aiApi for backward compatibility, pointing to datasource
export const aiApi = datasourceApi;

// Movie datasource API
export const movieDatasourceApi = {
  fetchMovieCandidates: async (
    data: DatasourceRequest,
  ): Promise<DatasourceSearchResponse> => {
    const DATASOURCE_URL =
      import.meta.env.VITE_DATASOURCE_API_URL || `${API_URL}/api/v1/datasource`;
    const response = await fetch(`${DATASOURCE_URL}/movie/search`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  },

  fetchMovieDetailsStreaming: async (
    data: DatasourceDetailsRequest,
    onProgress?: (message: string) => void,
  ): Promise<MovieInfoResponse> => {
    const DATASOURCE_URL =
      import.meta.env.VITE_DATASOURCE_API_URL || `${API_URL}/api/v1/datasource`;
    const response = await fetch(`${DATASOURCE_URL}/movie/details/stream`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    return readDatasourceStream(response, normalizeMovieInfo, onProgress);
  },

  fetchMovieInfoStreaming: async (
    data: DatasourceRequest,
    onProgress?: (message: string) => void,
  ): Promise<MovieInfoResponse> => {
    const DATASOURCE_URL =
      import.meta.env.VITE_DATASOURCE_API_URL || `${API_URL}/api/v1/datasource`;
    const response = await fetch(`${DATASOURCE_URL}/movie/stream`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    return readDatasourceStream(response, normalizeMovieInfo, onProgress);
  },
};

// Series datasource API// Series datasource API
export const seriesDatasourceApi = {
  fetchSeriesCandidates: async (
    data: DatasourceRequest,
  ): Promise<DatasourceSearchResponse> => {
    const DATASOURCE_URL =
      import.meta.env.VITE_DATASOURCE_API_URL || `${API_URL}/api/v1/datasource`;
    const response = await fetch(`${DATASOURCE_URL}/series/search`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  },

  fetchSeriesDetailsStreaming: async (
    data: DatasourceDetailsRequest,
    onProgress?: (message: string) => void,
  ): Promise<SeriesInfoResponse> => {
    const DATASOURCE_URL =
      import.meta.env.VITE_DATASOURCE_API_URL || `${API_URL}/api/v1/datasource`;
    const response = await fetch(`${DATASOURCE_URL}/series/details/stream`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    return readDatasourceStream(response, normalizeSeriesInfo, onProgress);
  },

  fetchSeriesInfoStreaming: async (
    data: DatasourceRequest,
    onProgress?: (message: string) => void,
  ): Promise<SeriesInfoResponse> => {
    const DATASOURCE_URL =
      import.meta.env.VITE_DATASOURCE_API_URL || `${API_URL}/api/v1/datasource`;
    const response = await fetch(`${DATASOURCE_URL}/series/stream`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    return readDatasourceStream(response, normalizeSeriesInfo, onProgress);
  },
};

export const authorsApi = {
  list: (params?: PaginationParams) =>
    fetchPaginated<Author>("/authors", params),
  get: (id: string) => fetchOne<Author>("/authors", id),
  create: (data: AuthorCreate) =>
    createOne<Author, AuthorCreate>("/authors", data),
  update: (id: string, data: AuthorUpdate) =>
    updateOne<Author, AuthorUpdate>("/authors", id, data),
  delete: (id: string) => deleteOne("/authors", id),
};

// =============================================================================
// Publishers API
// =============================================================================

export const publishersApi = {
  list: (params?: PaginationParams) =>
    fetchPaginated<Publisher>("/publishers", params),
  get: (id: string) => fetchOne<Publisher>("/publishers", id),
  create: (data: PublisherCreate) =>
    createOne<Publisher, PublisherCreate>("/publishers", data),
  update: (id: string, data: PublisherUpdate) =>
    updateOne<Publisher, PublisherUpdate>("/publishers", id, data),
  delete: (id: string) => deleteOne("/publishers", id),
};

// =============================================================================
// Directors API
// =============================================================================

export const directorsApi = {
  list: (params?: PaginationParams) =>
    fetchPaginated<Director>("/directors", params),
  get: (id: string) => fetchOne<Director>("/directors", id),
  create: (data: DirectorCreate) =>
    createOne<Director, DirectorCreate>("/directors", data),
  update: (id: string, data: DirectorUpdate) =>
    updateOne<Director, DirectorUpdate>("/directors", id, data),
  delete: (id: string) => deleteOne("/directors", id),
};

// =============================================================================
// Audit Logs API
// =============================================================================

export const auditLogsApi = {
  list: async (params?: { limit?: number }): Promise<AuditLog[]> => {
    const { data } = await apiClient.get<AuditLog[]>("/audit-logs", {
      params: {
        limit: params?.limit || 50,
      },
    });
    return data;
  },
};

// =============================================================================
// Images API
// =============================================================================

export const imagesApi = {
  list: async (params?: PaginationParams & { category_id?: string }) => {
    const { data } = await apiClient.get<PaginatedResponse<Image>>("/images", {
      params: {
        page: params?.page || 1,
        page_size: params?.page_size || 50,
        category_id: params?.category_id,
      },
    });
    return data;
  },

  upload: async (
    file: File,
    title?: string,
    category_id?: string,
  ): Promise<Image> => {
    const formData = new FormData();
    formData.append("file", file);
    if (title) formData.append("title", title);
    if (category_id) formData.append("category_id", category_id);
    const { data } = await apiClient.post<Image>("/images/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return data;
  },

  uploadFromUrl: async (
    url: string,
    title?: string,
    category_id?: string,
  ): Promise<Image> => {
    const formData = new FormData();
    formData.append("url", url);
    if (title) formData.append("title", title);
    if (category_id) formData.append("category_id", category_id);
    const { data } = await apiClient.post<Image>(
      "/images/upload-from-url",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );
    return data;
  },

  get: (id: string) => fetchOne<Image>("/images", id),
  
  update: async (id: string, data: { title?: string }): Promise<Image> => {
    const { data: response } = await apiClient.patch<Image>(`/images/${id}`, data);
    return response;
  },
  
  delete: (id: string) => deleteOne("/images", id),

  getFileUrl: (imageId: string): string => {
    return `${API_URL}/api/v1/images/${imageId}/file`;
  },

  getPublicImageUrl: (imageId: string): string => {
    return `${API_URL}/api/v1/public/images/${imageId}/content`;
  },
};

// =============================================================================
// Cinemas API
// =============================================================================

export const cinemasApi = {
  list: (params?: PaginationParams) =>
    fetchPaginated<Cinema>("/cinemas", params),
  get: (id: string) => fetchOne<Cinema>("/cinemas", id),
  create: (data: CinemaCreate) =>
    createOne<Cinema, CinemaCreate>("/cinemas", data),
  update: (id: string, data: CinemaUpdate) =>
    updateOne<Cinema, CinemaUpdate>("/cinemas", id, data),
  delete: (id: string) => deleteOne("/cinemas", id),
};

// =============================================================================
// Subscription Types API
// =============================================================================

export const subscriptionTypesApi = {
  list: (params?: PaginationParams) =>
    fetchPaginated<SubscriptionType>("/subscription-types", params),
  get: (id: string) => fetchOne<SubscriptionType>("/subscription-types", id),
  create: (data: SubscriptionTypeCreate) =>
    createOne<SubscriptionType, SubscriptionTypeCreate>("/subscription-types", data),
  update: (id: string, data: SubscriptionTypeUpdate) =>
    updateOne<SubscriptionType, SubscriptionTypeUpdate>("/subscription-types", id, data),
  delete: (id: string) => deleteOne("/subscription-types", id),
};

export const cinemaHallsApi = {
  listByCinema: async (cinemaId: string): Promise<CinemaHall[]> => {
    const { data } = await apiClient.get<CinemaHall[]>(
      `/cinemas/${cinemaId}/halls`,
    );
    return data;
  },
  create: async (
    cinemaId: string,
    data: Omit<CinemaHallCreate, "cinema_id">,
  ): Promise<CinemaHall> => {
    const { data: hall } = await apiClient.post<CinemaHall>(
      `/cinemas/${cinemaId}/halls`,
      { ...data, cinema_id: cinemaId },
    );
    return hall;
  },
  update: async (
    hallId: string,
    data: CinemaHallUpdate,
  ): Promise<CinemaHall> => {
    const { data: hall } = await apiClient.patch<CinemaHall>(
      `/cinemas/halls/${hallId}`,
      data,
    );
    return hall;
  },
  delete: async (hallId: string): Promise<void> => {
    await apiClient.delete(`/cinemas/halls/${hallId}`);
  },
};
export interface SystemContainerStatus {
  id: string;
  name: string;
  image: string;
  state: string;
  status: string;
  memory_usage_bytes: number;
  memory_limit_bytes: number;
  memory_percent: number;
  cpu_percent: number;
  disk_writable_bytes: number;
  disk_root_bytes: number;
  metrics_error?: string;
}

export interface SystemStatusResponse {
  generated_at: string;
  database: {
    name: string;
    size_bytes: number;
    connections: number;
    max_connections: number;
    tables: Array<{ table: string; rows: number }>;
  };
  storage: {
    path: string;
    total_bytes: number;
    used_bytes: number;
    free_bytes: number;
    used_percent: number;
  };
  docker: {
    available: boolean;
    containers: SystemContainerStatus[];
    error?: string | null;
  };
}

export interface TrafficStatsResponse {
  days: number;
  totals: Record<'human' | 'search_bot' | 'ai_bot' | 'other_bot', number>;
  unique_humans: number;
  daily: Array<{
    date: string;
    human: number;
    search_bot: number;
    ai_bot: number;
    other_bot: number;
  }>;
  top_paths: Array<{ path: string; visits: number }>;
  bots: Array<{ visitor_type: string; name: string; visits: number }>;
  synced_bot_events: number;
}

export const systemApi = {
  status: async (): Promise<SystemStatusResponse> => {
    const { data } = await apiClient.get<SystemStatusResponse>('/system/status', {
      timeout: 30000,
    });
    return data;
  },
  traffic: async (days: number): Promise<TrafficStatsResponse> => {
    const { data } = await apiClient.get<TrafficStatsResponse>('/system/traffic', {
      params: { days },
      timeout: 30000,
    });
    return data;
  },
};

export default apiClient;
